import './styles.css';

import { Extension, mergeAttributes, Node } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Markdown } from 'tiptap-markdown';

import { StaggerExtension } from './tiptap-extensions/StaggerExtension';

/**
 * Find the best chunk boundary in text (newline > sentence > word)
 * to avoid cutting in the middle of markdown syntax
 */
function findChunkBoundary(
  text: string,
  startPos: number,
  targetChunkSize: number,
): number {
  const searchEnd = Math.min(startPos + targetChunkSize * 2, text.length);
  const searchRegion = text.slice(startPos, searchEnd);

  // First, look for a newline within the target range (best boundary for markdown)
  const newlineMatch = searchRegion.match(/\n/);
  if (newlineMatch && newlineMatch.index !== undefined) {
    const boundaryPos = startPos + newlineMatch.index + 1;
    // Only use if it's at least 20% of target size (avoid tiny chunks)
    if (boundaryPos - startPos >= targetChunkSize * 0.2) {
      return boundaryPos;
    }
  }

  // Next, look for sentence endings (. ! ?) followed by space
  const sentenceMatch = searchRegion.match(/[.!?]\s/);
  if (
    sentenceMatch &&
    sentenceMatch.index !== undefined &&
    sentenceMatch.index <= targetChunkSize * 1.3
  ) {
    return startPos + sentenceMatch.index + 2;
  }

  // Fall back to word boundary (space)
  const targetEnd = startPos + targetChunkSize;
  if (targetEnd >= text.length) {
    return text.length;
  }

  // Find the last space before or near the target
  const nearTarget = text.slice(startPos, targetEnd + 20);
  let lastSpace = nearTarget.lastIndexOf(' ', targetChunkSize);

  // If no space found before target, look for next space after
  if (lastSpace === -1 || lastSpace < targetChunkSize * 0.5) {
    const nextSpace = nearTarget.indexOf(' ', targetChunkSize);
    if (nextSpace !== -1) {
      lastSpace = nextSpace;
    }
  }

  if (lastSpace !== -1) {
    return startPos + lastSpace + 1;
  }

  // Last resort: just use character count
  return Math.min(targetEnd, text.length);
}

/**
 * Hook that buffers content and releases it in smooth chunks
 * Creates a typewriter-like effect for streaming content
 * Chunks on markdown-safe boundaries (newlines, sentences, words)
 */
function useStreamingBuffer(
  targetContent: string,
  isStreaming: boolean,
  options: {
    chunkSize?: number;
    intervalMs?: number;
  } = {},
) {
  const { chunkSize = 32, intervalMs = 120 } = options;

  const [displayedContent, setDisplayedContent] = useState(targetContent);
  const bufferRef = useRef(targetContent);
  const displayedRef = useRef(targetContent);
  const rafIdRef = useRef<number | null>(null);
  const lastUpdateRef = useRef(performance.now());

  // Update buffer when target content changes
  useEffect(() => {
    bufferRef.current = targetContent;

    // If not streaming, immediately show all content
    if (!isStreaming) {
      displayedRef.current = targetContent;
      setDisplayedContent(targetContent);
    }
  }, [targetContent, isStreaming]);

  // Animation loop to gradually release buffered content
  const tick = useCallback(() => {
    const now = performance.now();
    const elapsed = now - lastUpdateRef.current;

    if (elapsed >= intervalMs) {
      const buffer = bufferRef.current;
      const displayed = displayedRef.current;

      // Check if we have content to release
      if (buffer.length > displayed.length && buffer.startsWith(displayed)) {
        // Find the best boundary for the next chunk
        const boundaryPos = findChunkBoundary(
          buffer,
          displayed.length,
          chunkSize,
        );

        const newDisplayed = buffer.slice(0, boundaryPos);
        displayedRef.current = newDisplayed;
        setDisplayedContent(newDisplayed);
        lastUpdateRef.current = now;
      } else if (buffer !== displayed) {
        // Content was replaced (not appended), update immediately
        displayedRef.current = buffer;
        setDisplayedContent(buffer);
        lastUpdateRef.current = now;
      }
    }

    rafIdRef.current = requestAnimationFrame(tick);
  }, [chunkSize, intervalMs]);

  // Start/stop the animation loop based on streaming state
  useEffect(() => {
    if (isStreaming) {
      rafIdRef.current = requestAnimationFrame(tick);
    }

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [isStreaming, tick]);

  // When streaming stops, flush remaining content
  useEffect(() => {
    if (!isStreaming && displayedRef.current !== bufferRef.current) {
      displayedRef.current = bufferRef.current;
      setDisplayedContent(bufferRef.current);
    }
  }, [isStreaming]);

  return displayedContent;
}

export const MIN_COLUMN_WIDTH = 100;
export const CustomTable = Table.extend({
  renderHTML({ node, HTMLAttributes }) {
    // Get the first row to count columns
    const firstRow = node.content.firstChild;
    const columnCount = firstRow?.childCount || 1;

    // Calculate minimum width based on number of columns
    // Using 200px as minimum width per column
    const minWidth = Math.max(columnCount * MIN_COLUMN_WIDTH, 400);

    return [
      'div',
      { class: 'tableWrapper' },
      [
        'table',
        mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
          style: `min-width: ${minWidth}px;`,
        }),
        0,
      ],
    ];
  },
});

export const MessageContentViewer = ({
  content,
  extensions,
  isStreaming = false,
}: MessageContentViewerProps) => {
  // Buffer content for smooth streaming release
  // Now chunks on smart boundaries (newlines > sentences > words)
  const displayedContent = useStreamingBuffer(content, isStreaming, {
    chunkSize: 100, // Target ~1-2 sentences (will break on natural boundaries)
    intervalMs: 200, // Deliberate pacing - visible chunk updates
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Markdown,
      Link.configure({
        openOnClick: true,
        autolink: true,
        HTMLAttributes: {
          class: 'text-blue-600 underline cursor-pointer',
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      CustomTable.configure({
        resizable: false,
        cellMinWidth: MIN_COLUMN_WIDTH,
      }),
      TableRow,
      TableCell,
      TableHeader,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      StaggerExtension.configure({
        enabled: isStreaming,
      }),
      ...(extensions || []),
    ],
    content: displayedContent,
    editable: false,
  });

  // Update editor content when buffered content changes
  // Use queueMicrotask to avoid flushSync warning during React render
  useEffect(() => {
    if (editor && displayedContent) {
      queueMicrotask(() => {
        editor.commands.setContent(displayedContent);
      });
    }
  }, [editor, displayedContent]);

  // Enable/disable stagger animation based on streaming state
  useEffect(() => {
    if (editor) {
      queueMicrotask(() => {
        editor.commands.setStaggerEnabled(isStreaming);
      });
    }
  }, [editor, isStreaming]);

  return <EditorContent editor={editor} />;
};
export interface MessageContentViewerProps {
  content: string;
  extensions?: (Extension | Node)[];
  /** Enable streaming animation for new content */
  isStreaming?: boolean;
}
